/**
 * Pestaña "Pagos" en la ficha del contrato individual.
 *
 * Muestra el timeline completo de cuotas planificadas con:
 * - Fecha prevista de cobro y estado visual
 * - Barra de progreso
 * - Marcar como pagado (pending/overdue → paid) con método + nota
 * - Editar cualquier cuota: cambio de estado (paid/pending/overdue/cancelled),
 *   método de pago, nota y deshacer un pago
 * - Sincronización con Stripe
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  BanknotesIcon,
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  CurrencyEuroIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Modal } from '@/components/common/Modal';
import { contractsService } from '@/services/contractsService';
import { formatCurrency } from '@/utils/formatters';
import type { Contract, ContractInstallment, InstallmentStatus } from '@/types/contracts';
import type { StripeBillingSummary } from '@/types/stripe';
import { toast } from 'sonner';

export const PAYMENT_METHODS = ['Tarjeta', 'Transferencia', 'Bizum'] as const;
export type PaymentMethodOption = (typeof PAYMENT_METHODS)[number];

const ALL_STATUSES: { value: InstallmentStatus; labelKey: string }[] = [
  { value: 'paid', labelKey: 'contractPayments.paid' },
  { value: 'pending', labelKey: 'contractPayments.pending' },
  { value: 'overdue', labelKey: 'contractPayments.overdue' },
  { value: 'cancelled', labelKey: 'contractPayments.canceled' },
];

const STATUS_CONFIG: Record<
  InstallmentStatus,
  { badge: string; border: string; icon: React.ElementType; labelKey: string }
> = {
  paid: {
    badge: 'bg-green-100 text-green-800 border-green-200',
    border: 'border-green-200 bg-green-50/50',
    icon: CheckCircleSolidIcon,
    labelKey: 'contractPayments.paid',
  },
  pending: {
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    border: 'border-gray-200 bg-gray-50/30',
    icon: ClockIcon,
    labelKey: 'contractPayments.pending',
  },
  overdue: {
    badge: 'bg-red-100 text-red-800 border-red-200',
    border: 'border-red-300 bg-red-50/30',
    icon: ExclamationTriangleIcon,
    labelKey: 'contractPayments.overdue',
  },
  cancelled: {
    badge: 'bg-gray-100 text-gray-600 border-gray-200',
    border: 'border-gray-200 bg-gray-50/20',
    icon: XCircleIcon,
    labelKey: 'contractPayments.canceled',
  },
};

interface ModalState {
  number: number;
  mode: 'mark-paid' | 'edit';
}

interface ContractPaymentsTabProps {
  contract: Contract;
  stripeSummary: StripeBillingSummary | null | undefined;
  onContractUpdate: () => void;
}

export function ContractPaymentsTab({
  contract,
  stripeSummary: _stripeSummary,
  onContractUpdate,
}: ContractPaymentsTabProps) {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const [installments, setInstallments] = useState<ContractInstallment[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Campos del formulario del modal
  const [method, setMethod] = useState<PaymentMethodOption>('Tarjeta');
  const [note, setNote] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<InstallmentStatus>('paid');

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------
  const fetchInstallments = useCallback(async () => {
    if (!contract.hiring_code) return;
    try {
      const data = await contractsService.getInstallments(contract.hiring_code);
      setInstallments(data);
    } catch {
      setInstallments([]);
    } finally {
      setLoading(false);
    }
  }, [contract.hiring_code]);

  useEffect(() => {
    fetchInstallments();
  }, [fetchInstallments]);

  // ---------------------------------------------------------------------------
  // Modal helpers
  // ---------------------------------------------------------------------------
  const openMarkPaid = useCallback(
    (number: number) => {
      setModal({ number, mode: 'mark-paid' });
      setSelectedStatus('paid');
      setMethod('Tarjeta');
      setNote('');
    },
    [],
  );

  const openEdit = useCallback(
    (inst: ContractInstallment) => {
      setModal({ number: inst.installment_number, mode: 'edit' });
      setSelectedStatus(inst.status);
      setMethod((inst.payment_method as PaymentMethodOption) ?? 'Tarjeta');
      setNote(inst.note ?? '');
    },
    [],
  );

  const closeModal = useCallback(() => {
    setModal(null);
    setNote('');
  }, []);

  const currentInstallment = useMemo(
    () =>
      modal ? installments.find((i) => i.installment_number === modal.number) ?? null : null,
    [modal, installments],
  );

  // ---------------------------------------------------------------------------
  // Acciones
  // ---------------------------------------------------------------------------
  const handleMarkAsPaid = useCallback(async () => {
    if (!modal || !contract.hiring_code) return;
    setSaving(true);
    try {
      await contractsService.markInstallmentPaid(contract.hiring_code, modal.number, {
        payment_method: method,
        note: note.trim() || undefined,
      });
      toast.success(`Cuota ${modal.number} marcada como pagada (${method}).`);
      closeModal();
      await fetchInstallments();
      onContractUpdate();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : undefined) ?? e?.message ?? 'Error al guardar.',
      );
    } finally {
      setSaving(false);
    }
  }, [contract.hiring_code, modal, method, note, closeModal, fetchInstallments, onContractUpdate]);

  const handleEditSave = useCallback(async () => {
    if (!modal || !contract.hiring_code) return;
    setSaving(true);
    try {
      await contractsService.updateInstallment(contract.hiring_code, modal.number, {
        status: selectedStatus,
        payment_method: method || undefined,
        note: note.trim() || undefined,
      });
      toast.success(`Cuota ${modal.number} actualizada a "${t(STATUS_CONFIG[selectedStatus].labelKey)}".`);
      closeModal();
      await fetchInstallments();
      onContractUpdate();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : undefined) ?? e?.message ?? 'Error al guardar.',
      );
    } finally {
      setSaving(false);
    }
  }, [
    contract.hiring_code,
    modal,
    selectedStatus,
    method,
    note,
    closeModal,
    fetchInstallments,
    onContractUpdate,
  ]);

  const handleSyncStripe = useCallback(async () => {
    if (!contract.hiring_code) return;
    setSyncing(true);
    try {
      await contractsService.syncStripeInstallments(contract.hiring_code);
      toast.success('Cuotas sincronizadas con Stripe.');
      await fetchInstallments();
      onContractUpdate();
    } catch {
      toast.error('Error al sincronizar con Stripe.');
    } finally {
      setSyncing(false);
    }
  }, [contract.hiring_code, fetchInstallments, onContractUpdate]);

  // ---------------------------------------------------------------------------
  // Crear factura desde cuota
  // ---------------------------------------------------------------------------
  const handleCreateInvoice = useCallback(
    (inst: ContractInstallment) => {
      const params = new URLSearchParams({
        client_name: contract.client_name || '',
        client_email: contract.client_email || '',
        client_tax_id: contract.client_nie || contract.client_passport || '',
        hiring_payment_id: String(inst.hiring_payment_id),
        contract_installment_id: String(inst.id),
        hiring_code: contract.hiring_code || '',
        amount_cents: String(inst.amount_cents),
        description: `${contract.service_name || 'Servicio'} - Cuota ${inst.installment_number}/${inst.total_installments}`,
        currency: inst.currency || contract.currency || 'EUR',
      });
      navigate(`/crm/facturas/crear?${params.toString()}`);
    },
    [contract, navigate],
  );

  const handleViewInvoice = useCallback(
    (invoiceId: string) => {
      navigate(`/crm/facturas/${invoiceId}`);
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Estadísticas
  // ---------------------------------------------------------------------------
  const paidCount = useMemo(() => installments.filter((i) => i.status === 'paid').length, [installments]);
  const overdueCount = useMemo(() => installments.filter((i) => i.status === 'overdue').length, [installments]);
  const nextDue = useMemo(() => {
    const pending = installments
      .filter((i) => i.status === 'pending' || i.status === 'overdue')
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
    return pending[0] ?? null;
  }, [installments]);

  const progressPercent =
    installments.length > 0 ? Math.round((paidCount / installments.length) * 100) : 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <Card data-testid="contract-payments-tab">
        <CardContent className="py-8 text-center text-gray-500">Cargando cuotas…</CardContent>
      </Card>
    );
  }

  if (installments.length === 0) {
    return (
      <Card data-testid="contract-payments-tab">
        <CardContent className="py-8 text-center text-gray-500">
          No hay cuotas planificadas para este contrato.
        </CardContent>
      </Card>
    );
  }

  const modalTitle =
    modal?.mode === 'edit' && currentInstallment
      ? `Editar cuota ${modal.number} de ${currentInstallment.total_installments}`
      : modal
        ? `Marcar cuota ${modal.number} de ${currentInstallment?.total_installments ?? ''} como pagada`
        : '';

  return (
    <>
      <Card data-testid="contract-payments-tab">
        <CardHeader>
          <CardTitle
            className="flex items-center gap-2"
            data-testid="contract-payments-title"
          >
            <BanknotesIcon width={20} height={20} />
            Evolución de pagos
          </CardTitle>

          {/* Resumen */}
          <div className="space-y-2 mt-1">
            <p className="text-sm text-gray-600">
              {paidCount} de {installments.length} pagos realizados
              {overdueCount > 0 && (
                <span className="text-red-600 font-medium"> · {overdueCount} vencidos</span>
              )}
              {nextDue && (
                <span className="text-gray-500">
                  {' · '}Próximo cobro:{' '}
                  {new Date(nextDue.due_date).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              )}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <ul className="space-y-2" role="list">
            {installments.map((inst) => {
              const cfg = STATUS_CONFIG[inst.status] ?? STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              const amountEur = inst.amount_cents / 100;
              const dueFormatted = new Date(inst.due_date).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });

              return (
                <li
                  key={inst.id}
                  data-testid={`payment-slot-${inst.installment_number}`}
                  data-payment-status={inst.status}
                  className={`flex flex-col gap-2 rounded-lg border p-3 min-h-[44px] ${cfg.border}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* Izquierda: número, importe, badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        Pago {inst.installment_number} de {inst.total_installments}
                      </span>
                      <span className="text-sm text-gray-600">
                        {formatCurrency(amountEur, contract.currency ?? 'eur', false)}
                      </span>
                      <Badge className={cfg.badge}>
                        <StatusIcon className="w-3.5 h-3.5 mr-1 inline" />
                        {t(cfg.labelKey)}
                      </Badge>
                    </div>

                    {/* Derecha: fecha prevista + info pago + acciones */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDaysIcon className="w-4 h-4" />
                        {dueFormatted}
                      </span>

                      {inst.status === 'paid' && (
                        <>
                          {inst.payment_method && (
                            <span className="inline-flex items-center gap-1">
                              {inst.payment_method === 'Tarjeta' && (
                                <CreditCardIcon className="w-4 h-4" />
                              )}
                              {inst.payment_method === 'Transferencia' && (
                                <CurrencyEuroIcon className="w-4 h-4" />
                              )}
                              {inst.payment_method === 'Bizum' && (
                                <BanknotesIcon className="w-4 h-4" />
                              )}
                              {inst.payment_method}
                            </span>
                          )}
                          {inst.paid_at && (
                            <span>
                              {new Date(inst.paid_at).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </span>
                          )}
                          {inst.payment_source === 'stripe' && (
                            <Badge variant="outline" className="text-xs">
                              Stripe
                            </Badge>
                          )}
                        </>
                      )}

                      {/* Marcar como pagado (solo pending/overdue) */}
                      {(inst.status === 'pending' || inst.status === 'overdue') && (
                        <Button
                          type="button"
                          data-testid={`mark-payment-paid-${inst.installment_number}`}
                          variant="outline"
                          size="sm"
                          onClick={() => openMarkPaid(inst.installment_number)}
                          className="min-h-[44px] touch-manipulation"
                        >
                          <PencilSquareIcon className="w-4 h-4 mr-1" />
                          Marcar pagado
                        </Button>
                      )}

                      {/* Ver / Crear factura (cuotas pagadas sin factura Stripe) */}
                      {inst.status === 'paid' && !inst.stripe_invoice_id && (
                        inst.invoice_id ? (
                          <Button
                            type="button"
                            data-testid={`view-invoice-${inst.installment_number}`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewInvoice(inst.invoice_id!)}
                            className="min-h-[44px] touch-manipulation"
                            title={
                              inst.invoice_number
                                ? `Ver factura ${inst.invoice_number}`
                                : 'Ver factura vinculada a este pago'
                            }
                          >
                            <DocumentTextIcon className="w-4 h-4 mr-1" />
                            Ver factura
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            data-testid={`create-invoice-${inst.installment_number}`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleCreateInvoice(inst)}
                            className="min-h-[44px] touch-manipulation"
                            title="Crear factura para este pago"
                          >
                            <DocumentTextIcon className="w-4 h-4 mr-1" />
                            Crear factura
                          </Button>
                        )
                      )}

                      {/* Editar (todos los estados) */}
                      <Button
                        type="button"
                        data-testid={`edit-installment-${inst.installment_number}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(inst)}
                        className="min-h-[44px] touch-manipulation text-gray-500 hover:text-gray-800"
                        title="Editar estado o datos de esta cuota"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Nota */}
                  {inst.note && (
                    <p className="text-xs text-gray-500 italic">{inst.note}</p>
                  )}
                  {inst.updated_by && (
                    <p className="text-xs text-gray-400">Actualizado por: {inst.updated_by}</p>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Sync Stripe */}
          {contract.subscription_id && (
            <div className="pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSyncStripe}
                disabled={syncing}
                className="text-xs"
              >
                {syncing ? 'Sincronizando…' : 'Sincronizar con Stripe'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Modal: Marcar como pagado                                           */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={modal?.mode === 'mark-paid'}
        onClose={closeModal}
        title={modalTitle}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button
              data-testid="mark-payment-paid-submit"
              onClick={handleMarkAsPaid}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="payment-method">Forma de pago</Label>
            <select
              id="payment-method"
              data-testid="payment-method-select"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethodOption)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="payment-note">Nota (opcional)</Label>
            <Textarea
              id="payment-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: Referencia transferencia, últimos 4 dígitos tarjeta…"
              rows={2}
              className="mt-1"
            />
          </div>
        </div>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Modal: Editar cuota (cualquier estado)                              */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={modal?.mode === 'edit'}
        onClose={closeModal}
        title={modalTitle}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button
              data-testid="edit-installment-submit"
              onClick={handleEditSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Estado */}
          <div>
            <Label htmlFor="edit-status">Estado</Label>
            <select
              id="edit-status"
              data-testid="edit-status-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as InstallmentStatus)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ALL_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.labelKey)}
                </option>
              ))}
            </select>
            {selectedStatus !== 'paid' && currentInstallment?.status === 'paid' && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Cambiar de "Pagado" borrará la fecha y método de pago registrados.
              </p>
            )}
          </div>

          {/* Forma de pago (solo si estado = paid) */}
          {selectedStatus === 'paid' && (
            <div>
              <Label htmlFor="edit-payment-method">Forma de pago</Label>
              <select
                id="edit-payment-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as PaymentMethodOption)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Nota */}
          <div>
            <Label htmlFor="edit-note">Nota (opcional)</Label>
            <Textarea
              id="edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Observaciones sobre esta cuota…"
              rows={2}
              className="mt-1"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
